/**
 * S2 (AB#2205, SG-06 / SG-17) — a staff member's home: My Day.
 *
 * Everything here is what GET /salon-staff/my-day and /salon-staff/me return:
 * the salon-local day, the shift (working / day off / on leave), the next
 * booking, the pending-action counts and "what changed" are decided by the
 * backend. The page only renders them; bookings open the shared S1 staff detail
 * whose actions are the backend's `allowedTransitions`.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../../contexts/LanguageContext';
import { useNotifications } from '../../../contexts/NotificationsContext';
import { useStaffMe, useStaffMyDay } from '../../../hooks/useStaffSelf';
import BookingActions from '../../../components/BookingDetail/BookingActions';
import BookingStatusPill from '../../../components/BookingDetail/BookingStatusPill';
import { addMinutesHHmm, StaffBookingFlow } from '../../../components/BookingDetail/staffBooking';
import { toLifecycleStatus } from '../../../lib/bookingLifecycle';
import { formatDurationMinutes } from '../../../lib/humanDuration';
import {
    absenceTypeLabel,
    branchesText,
    fill,
    formatDayDate,
    isolate,
    localizedName,
    staffIdentityLine,
    timeRange,
} from '../../../lib/staffDisplay';
import {
    isReassignedAway,
    isStaffBookingNotificationType,
    parseStaffBookingData,
    staffBookingNotificationText,
} from '../../../lib/staffNotifications';
import type { SalonStaffAppointmentRowDto } from '../../../services/appointmentsList';
import type { StaffDayShift, StaffMyDayDto, StaffRecentChange } from '../../../types/staffSelf';
import s from './MyDay.module.css';

type TFn = (key: string) => string;

/** Service name in the UI language when the row carries an Arabic name. */
function rowServiceName(row: SalonStaffAppointmentRowDto, language: string): string {
    const ar = (row as SalonStaffAppointmentRowDto & { serviceNameAr?: string | null }).serviceNameAr;
    return localizedName(row.serviceName, ar, language);
}

function rowTime(row: SalonStaffAppointmentRowDto): string {
    const start = row.branchLocalStartTime;
    return row.durationMinutes != null ? timeRange(start, addMinutesHHmm(start, row.durationMinutes)) : timeRange(start, null);
}

/* ── Shift ─────────────────────────────────────────────────────────── */
const ShiftCard: React.FC<{ shift: StaffDayShift | null | undefined; language: string; t: TFn }> = ({ shift, language, t }) => {
    let body: React.ReactNode;
    if (!shift) {
        body = <p className={s.muted}>{t('stf.shift.unknown')}</p>;
    } else if (shift.status === 'absent') {
        const a = shift.absence;
        body = (
            <>
                <p className={s.shiftState} data-testid="shift-state" data-status="absent">
                    {fill(t('stf.shift.onLeave'), { type: absenceTypeLabel(a?.type, t, a?.typeLabelKey) })}
                </p>
                {a && !a.isFullDay && (a.start || a.end) && (
                    <p className={s.muted}>{fill(t('stf.shift.awayBetween'), { range: timeRange(a.start, a.end) })}</p>
                )}
                {a?.note && <p className={s.muted}><bdi dir="auto">{a.note}</bdi></p>}
            </>
        );
    } else if (shift.status === 'off') {
        body = (
            <p className={s.shiftState} data-testid="shift-state" data-status="off">
                {shift.salonClosed ? t('stf.shift.salonClosed') : t('stf.shift.off')}
            </p>
        );
    } else {
        const segments = shift.segments ?? [];
        // A working day can still carry an intra-day absence (break / emergency).
        const away = shift.absence && !shift.absence.isFullDay && (shift.absence.start || shift.absence.end)
            ? shift.absence
            : null;
        body = (
            <>
                <p className={s.shiftState} data-testid="shift-state" data-status="working">{t('stf.shift.working')}</p>
                {away && (
                    <p className={s.muted} data-testid="shift-away">
                        {absenceTypeLabel(away.type, t, away.typeLabelKey)}
                        {' · '}
                        {fill(t('stf.shift.awayBetween'), { range: timeRange(away.start, away.end) })}
                    </p>
                )}
                {segments.length > 0 && (
                    <ul className={s.segmentList}>
                        {segments.map((seg, i) => {
                            const branch = localizedName(seg.branchName, seg.branchNameAr, language);
                            return (
                                <li key={`${seg.branchId ?? 'b'}-${seg.start}-${i}`} className={s.segment}>
                                    <span className={s.time}>{timeRange(seg.start, seg.end)}</span>
                                    {branch && <span className={s.muted}>{branch}</span>}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </>
        );
    }
    return (
        <section className={s.card} aria-labelledby="myday-shift">
            <h2 id="myday-shift" className={s.cardTitle}>{t('stf.shift.title')}</h2>
            {body}
        </section>
    );
};

/* ── A booking line (next card + today's list) ─────────────────────── */
const BookingSummary: React.FC<{ row: SalonStaffAppointmentRowDto; language: string; t: TFn }> = ({ row, language, t }) => (
    <span className={s.summary}>
        <span className={s.summaryTop}>
            <span className={s.time}>{rowTime(row)}</span>
            {row.durationMinutes != null && (
                <span className={s.muted}>{formatDurationMinutes(row.durationMinutes, t)}</span>
            )}
        </span>
        <span className={s.summaryService}>{rowServiceName(row, language)}</span>
        <span className={s.muted}><bdi>{row.clientDisplayName || t('booking.detail.walkIn')}</bdi></span>
    </span>
);

/* ── What changed ──────────────────────────────────────────────────── */
/**
 * A change rendered from its notification `data` when present; otherwise from
 * the matching row of today's list (service + time); otherwise the type alone.
 */
function changeText(
    change: StaffRecentChange,
    rows: SalonStaffAppointmentRowDto[],
    language: string,
    t: TFn,
): { title: string; message: string } {
    if (!isStaffBookingNotificationType(change.type)) {
        return { title: t('stf.changes.generic'), message: '' };
    }
    const data = parseStaffBookingData(change.data);
    if (Object.keys(data).length > 0) {
        return staffBookingNotificationText(change.type, data, language, t);
    }
    const { title } = staffBookingNotificationText(change.type, null, language, t);
    const row = change.appointmentId ? rows.find(r => r.id === change.appointmentId) : undefined;
    return {
        title,
        message: row ? `${isolate(row.branchLocalStartTime)} · ${rowServiceName(row, language)}` : '',
    };
}

function changeTime(iso: string, language: string): string {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    // A notification's createdAt is a real instant — shown in the viewer's clock.
    return d.toLocaleString(language === 'ar' ? 'ar' : 'en-GB', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
    });
}

/* ── Page ──────────────────────────────────────────────────────────── */
const MyDay: React.FC = () => {
    const { t, language } = useLanguage();
    const me = useStaffMe();
    const day = useStaffMyDay();
    const { markAsRead } = useNotifications();
    const [openId, setOpenId] = useState<number | null>(null);

    const profile = me.data;
    const firstName = profile?.firstName || profile?.displayName?.trim().split(/\s+/)[0] || '';
    const greeting = firstName ? fill(t('stf.myDay.greeting'), { name: isolate(firstName) }) : t('stf.myDay.greetingNoName');
    const identity = staffIdentityLine(profile, language, t);
    const branches = branchesText(profile, language, t);

    const data: StaffMyDayDto | undefined = day.data;
    const rows = data?.appointments ?? [];
    const nextRow = data?.nextAppointmentId != null ? rows.find(r => r.id === data.nextAppointmentId) : undefined;
    const awaiting = data?.pendingActions?.awaitingConfirmation ?? 0;
    const unpaid = data?.pendingActions?.unpaidCompleted ?? 0;
    const payableToday = rows.filter(r => r.canMarkPaid);
    const counts = data?.counts;
    const changes = data?.recentChanges ?? [];
    const openRow = openId != null ? rows.find(r => r.id === openId) : undefined;

    const openChange = (change: StaffRecentChange) => {
        if (!change.isRead) void markAsRead(change.notificationId);
        // Reassigned away: the booking now belongs to someone else — nothing to open.
        if (change.appointmentId && !isReassignedAway(change.type, change.data)) setOpenId(change.appointmentId);
    };

    return (
        <div className={s.page}>
            <header className={s.header}>
                <div className={s.headerMain}>
                    <h1 className={s.title}>{greeting}</h1>
                    {(identity || branches) && (
                        <p className={s.identity} data-testid="myday-identity">
                            {[identity, branches, profile?.position ? isolate(profile.position) : ''].filter(Boolean).join(' · ')}
                        </p>
                    )}
                </div>
                {data?.date && (
                    <p className={s.date}>{formatDayDate(data.date, language, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
                )}
            </header>

            {day.isLoading && (
                <div className={s.skeleton} data-testid="myday-loading">
                    <span className={s.srOnly} role="status">{t('stf.loading')}</span>
                    <div className={s.skeletonGrid} aria-hidden="true">
                        <div className={s.skeletonCard} />
                        <div className={s.skeletonCard} />
                        <div className={s.skeletonCard} />
                    </div>
                    <div className={s.skeletonList} aria-hidden="true" />
                </div>
            )}

            {day.isError && !data && (
                <div className={s.errorBox} role="alert">
                    <p>{t('stf.myDay.loadFailed')}</p>
                    <button type="button" className={s.primaryBtn} onClick={() => { void day.refetch(); }}>
                        {t('stf.retry')}
                    </button>
                </div>
            )}

            {data && (
                <>
                    <div className={s.grid}>
                        <ShiftCard shift={data.shift} language={language} t={t} />

                        <section className={s.card} aria-labelledby="myday-next">
                            <h2 id="myday-next" className={s.cardTitle}>{t('stf.next.title')}</h2>
                            {nextRow ? (
                                <div className={s.nextBody} data-testid="myday-next">
                                    <BookingSummary row={nextRow} language={language} t={t} />
                                    <div className={s.nextFoot}>
                                        <BookingStatusPill status={nextRow.status} cancellationSource={nextRow.cancellationSource} />
                                        <button type="button" className={s.primaryBtn} onClick={() => setOpenId(nextRow.id)}>
                                            {t('stf.next.open')}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <p className={s.muted}>{t('stf.next.none')}</p>
                            )}
                        </section>

                        <section className={s.card} aria-labelledby="myday-pending">
                            <h2 id="myday-pending" className={s.cardTitle}>{t('stf.pending.title')}</h2>
                            {awaiting === 0 && unpaid === 0 && <p className={s.muted}>{t('stf.pending.none')}</p>}
                            {awaiting > 0 && (
                                <div className={s.pendingItem} data-testid="pending-awaiting">
                                    <span>{awaiting === 1 ? t('stf.pending.awaitingOne') : fill(t('stf.pending.awaitingMany'), { count: awaiting })}</span>
                                    <Link className={s.linkBtn} to="/appointments?tab=pending">{t('stf.pending.review')}</Link>
                                </div>
                            )}
                            {unpaid > 0 && (
                                <div className={s.pendingItem} data-testid="pending-unpaid">
                                    <span>{unpaid === 1 ? t('stf.pending.unpaidOne') : fill(t('stf.pending.unpaidMany'), { count: unpaid })}</span>
                                    {payableToday.map(r => (
                                        <button
                                            key={r.id}
                                            type="button"
                                            className={s.linkBtn}
                                            onClick={() => setOpenId(r.id)}
                                        >
                                            {fill(t('stf.pending.markPaidFor'), { time: isolate(r.branchLocalStartTime), service: rowServiceName(r, language) })}
                                        </button>
                                    ))}
                                    <Link className={s.linkBtn} to="/appointments?tab=past&payment=unpaid">{t('stf.pending.seeUnpaid')}</Link>
                                </div>
                            )}
                        </section>
                    </div>

                    <section className={s.section} aria-labelledby="myday-today">
                        <div className={s.sectionHead}>
                            <h2 id="myday-today" className={s.sectionTitle}>{t('stf.today.title')}</h2>
                            {counts && rows.length > 0 && (
                                <ul className={s.counts} aria-label={t('stf.counts.label')}>
                                    {([
                                        ['pending', counts.pending],
                                        ['confirmed', counts.confirmed],
                                        ['inVisit', counts.inVisit],
                                        ['completed', counts.completed],
                                        ['cancelled', counts.cancelledOrNoShow],
                                    ] as const).filter(([, n]) => (n ?? 0) > 0).map(([key, n]) => (
                                        <li key={key} className={`${s.countChip} ${s[`tone-${key}`] ?? ''}`}>
                                            {fill(t(`stf.counts.${key}`), { count: n ?? 0 })}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        {rows.length === 0 ? (
                            <p className={s.empty}>{t('stf.today.empty')}</p>
                        ) : (
                            <ul className={s.list} data-testid="myday-today">
                                {rows.map(row => (
                                    <li key={row.id} className={`${s.row} ${row.id === data.nextAppointmentId ? s.rowNext : ''}`}>
                                        <button
                                            type="button"
                                            className={s.rowMain}
                                            onClick={() => setOpenId(row.id)}
                                            aria-label={fill(t('stf.today.openNamed'), {
                                                time: isolate(row.branchLocalStartTime),
                                                service: rowServiceName(row, language),
                                            })}
                                        >
                                            <BookingSummary row={row} language={language} t={t} />
                                        </button>
                                        <div className={s.rowSide}>
                                            {row.id === data.nextAppointmentId && <span className={s.nextTag}>{t('stf.today.nextTag')}</span>}
                                            <BookingStatusPill status={row.status} cancellationSource={row.cancellationSource} />
                                            {toLifecycleStatus(row.status) === 'Pending' && (
                                                <BookingActions
                                                    appointmentId={row.id}
                                                    status={row.status}
                                                    allowedTransitions={row.allowedTransitions}
                                                    only={['Approved', 'Cancelled']}
                                                    compact
                                                    actor="staff"
                                                    onChanged={() => { void day.refetch(); }}
                                                />
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </section>

                    <section className={s.section} aria-labelledby="myday-changes">
                        <h2 id="myday-changes" className={s.sectionTitle}>{t('stf.changes.title')}</h2>
                        {changes.length === 0 ? (
                            <p className={s.empty}>{t('stf.changes.empty')}</p>
                        ) : (
                            <ul className={s.list} data-testid="myday-changes">
                                {changes.map(change => {
                                    const text = changeText(change, rows, language, t);
                                    const away = isReassignedAway(change.type, change.data);
                                    const content = (
                                        <span className={s.summary}>
                                            <span className={s.summaryTop}>
                                                {!change.isRead && (
                                                    <>
                                                        <span className={s.unreadDot} aria-hidden="true" />
                                                        <span className={s.srOnly}>{t('stf.changes.unread')}</span>
                                                    </>
                                                )}
                                                <span className={s.summaryService}>{text.title}</span>
                                            </span>
                                            {text.message && <span className={s.muted}>{text.message}</span>}
                                            {away && <span className={s.muted}>{t('stf.changes.noLongerYours')}</span>}
                                            <span className={s.muted}>{changeTime(change.createdAt, language)}</span>
                                        </span>
                                    );
                                    // Informational when there is nothing to open (reassigned away or no
                                    // booking) and nothing left to mark as read.
                                    const actionable = !change.isRead || (!!change.appointmentId && !away);
                                    return (
                                        <li key={change.notificationId} className={s.row} data-testid="myday-change">
                                            {actionable ? (
                                                <button
                                                    type="button"
                                                    className={s.rowMain}
                                                    onClick={() => openChange(change)}
                                                >
                                                    {content}
                                                </button>
                                            ) : (
                                                <div className={s.rowInfo}>{content}</div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </section>

                    <nav className={s.links} aria-label={t('stf.links.label')}>
                        <Link className={s.linkCard} to="/appointments">{t('stf.links.appointments')}</Link>
                        <Link className={s.linkCard} to="/schedule">{t('stf.links.schedule')}</Link>
                    </nav>
                </>
            )}

            {openId != null && (
                <StaffBookingFlow
                    appointmentId={openId}
                    row={openRow}
                    onClose={() => { setOpenId(null); void day.refetch(); }}
                />
            )}
        </div>
    );
};

export default MyDay;
